import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Users, Plus, AlertCircle, ExternalLink, Loader2, X, ChevronRight, Pencil, Search,
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import RolloverBadge from '../../components/RolloverBadge'
import { getAdminClients, createClient, getAdminClient, updateClient, getStores, getSlackChannels, searchXeroContacts, getRolloverSummary } from '../../lib/api'
import { ToastContainer, useToast } from '../../components/Toast'

function formatCurrency(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(v ?? 0)
}

function safeDate(d) {
  if (!d) return 'Never'
  try { return format(parseISO(d), 'dd MMM yyyy HH:mm') } catch { return d }
}

// ── Xero Contact Picker ───────────────────────────────────────────────────────

function XeroContactPicker({ value, onChange }) {
  const [showSearch, setShowSearch] = useState(false)
  const [search, setSearch] = useState('')

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['xero-contacts-search', search],
    queryFn: () => searchXeroContacts(search),
    enabled: search.length >= 2,
    staleTime: 30_000,
  })

  function selectContact(contactId) {
    onChange(contactId)
    setShowSearch(false)
    setSearch('')
  }

  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          className="input font-mono text-xs flex-1"
        />
        <button
          type="button"
          onClick={() => setShowSearch((s) => !s)}
          className="btn-secondary text-xs py-1 px-2 flex-shrink-0"
          title="Search Xero contacts by name"
        >
          <Search size={12} />
        </button>
      </div>
      {showSearch && (
        <div className="border border-slate-200 rounded-lg p-2 bg-white shadow-sm">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Type contact name…"
            className="input text-sm w-full"
            autoFocus
          />
          {isFetching && (
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <Loader2 size={10} className="animate-spin" /> Searching…
            </p>
          )}
          {!isFetching && search.length >= 2 && results.length === 0 && (
            <p className="text-xs text-slate-400 mt-1">No contacts found.</p>
          )}
          {results.length > 0 && (
            <ul className="mt-1 divide-y divide-slate-100 max-h-40 overflow-y-auto">
              {results.map((c) => (
                <li key={c.ContactID}>
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 text-sm hover:bg-blue-50 rounded flex items-center justify-between gap-2"
                    onClick={() => selectContact(c.ContactID)}
                  >
                    <span className="font-medium text-slate-800">{c.Name}</span>
                    <span className="text-slate-400 text-xs font-mono truncate">{c.ContactID}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function AddClientModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ name: '', nickname: '', email: '', xero_contact_id: '', store_ids: [] })
  const [errors, setErrors] = useState({})
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const { data: stores = [], isLoading: storesLoading } = useQuery({
    queryKey: ['admin', 'stores'],
    queryFn: getStores,
  })

  const mutation = useMutation({
    mutationFn: createClient,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores'] })
      onSuccess(data)
    },
    onError: (err) => {
      addToast({ type: 'error', message: err.response?.data?.detail || 'Failed to create client.' })
    },
  })

  function validate() {
    const e = {}
    if (!form.name.trim()) e.name = 'Company name is required'
    if (!form.email.trim()) {
      e.email = 'Email is required'
    } else {
      const addresses = form.email.split(',').map(s => s.trim()).filter(Boolean)
      const invalid = addresses.filter(a => !/\S+@\S+\.\S+/.test(a))
      if (invalid.length) e.email = `Invalid email${invalid.length > 1 ? 's' : ''}: ${invalid.join(', ')}`
    }
    return e
  }

  function toggleStore(storeId) {
    setForm(p => ({
      ...p,
      store_ids: p.store_ids.includes(storeId)
        ? p.store_ids.filter(id => id !== storeId)
        : [...p.store_ids, storeId]
    }))
  }

  function handleSubmit(e) {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }
    mutation.mutate({
      ...form,
      xero_contact_id: form.xero_contact_id || null,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Add New Client</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} noValidate className="flex flex-col flex-1 min-h-0">
          <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
            <div>
              <label className="label">Company Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Acme Corp Pty Ltd"
                className={`input ${errors.name ? 'border-red-300 focus:ring-red-400' : ''}`}
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="label">Nickname</label>
              <input
                type="text"
                value={form.nickname}
                onChange={(e) => setForm((p) => ({ ...p, nickname: e.target.value }))}
                placeholder="Glow Proteins"
                className="input"
              />
              <p className="text-xs text-slate-400 mt-1">Short display name used throughout the portal</p>
            </div>
            <div>
              <label className="label">Email Address(es) *</label>
              <input
                type="text"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="billing@client.com.au, accounts@client.com.au"
                className={`input ${errors.email ? 'border-red-300 focus:ring-red-400' : ''}`}
              />
              <p className="text-xs text-slate-400 mt-1">Separate multiple addresses with a comma</p>
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="label">Xero Contact ID</label>
              <XeroContactPicker
                value={form.xero_contact_id}
                onChange={(v) => setForm((p) => ({ ...p, xero_contact_id: v }))}
              />
              <p className="text-xs text-slate-400 mt-1">Optional — can be added later</p>
            </div>
            <div>
              <label className="label">ShipStation Stores</label>
              <p className="text-xs text-slate-400 mb-2">Select all stores that belong to this client. Multiple stores can be merged into one client.</p>
              {storesLoading ? (
                <div className="text-xs text-slate-400">Loading stores…</div>
              ) : (
                <>
                  {form.store_ids.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {form.store_ids.map(id => {
                        const store = stores.find(s => s.store_id === id)
                        if (!store) return null
                        return (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full"
                          >
                            {store.store_name}
                            <button
                              type="button"
                              onClick={() => toggleStore(id)}
                              className="text-blue-400 hover:text-blue-700 transition-colors"
                            >
                              <X size={11} />
                            </button>
                          </span>
                        )
                      })}
                    </div>
                  )}
                  {stores.filter(s => !form.store_ids.includes(s.store_id) && !s.assigned_client_id).length > 0 ? (
                    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-48 overflow-y-auto">
                      {stores
                        .filter(s => !form.store_ids.includes(s.store_id) && !s.assigned_client_id)
                        .map(store => (
                          <button
                            key={store.store_id}
                            type="button"
                            onClick={() => toggleStore(store.store_id)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-700">{store.store_name}</p>
                              <p className="text-xs text-slate-400">{store.marketplace_name}</p>
                            </div>
                            <Plus size={13} className="text-slate-300 flex-shrink-0" />
                          </button>
                        ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic">All stores assigned</p>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={mutation.isPending}>
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary">
              {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {mutation.isPending ? 'Creating…' : 'Create Client'}
            </button>
          </div>
        </form>
        <ToastContainer toasts={toasts} onClose={removeToast} />
      </div>
    </div>
  )
}

export function EditClientModal({ client, onClose, onSuccess }) {
  const [form, setForm] = useState(null)
  const [errors, setErrors] = useState({})
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const { data: stores = [], isLoading: storesLoading } = useQuery({
    queryKey: ['admin', 'stores'],
    queryFn: getStores,
  })

  // Load existing client detail (includes store_ids)
  const { data: clientData, isLoading: clientLoading } = useQuery({
    queryKey: ['admin', 'client', client.id],
    queryFn: () => getAdminClient(client.id),
  })

  const { data: slackChannels = [], isLoading: channelsLoading } = useQuery({
    queryKey: ['admin', 'slack-channels', client.id],
    queryFn: () => getSlackChannels(client.id),
    staleTime: 0,
  })

  useEffect(() => {
    if (clientData && !form) {
      setForm({
        name: clientData.name ?? '',
        nickname: clientData.nickname ?? '',
        email: clientData.email ?? '',
        xero_contact_id: clientData.xero_contact_id ?? '',
        store_ids: clientData.store_ids ?? [],
        slack_channel_id: clientData.slack_channel_id ?? '',
        slack_channel_name: clientData.slack_channel_name ?? '',
        termination_date: clientData.termination_date ?? '',
        archived: clientData.archived ?? false,
      })
    }
  }, [clientData])

  const mutation = useMutation({
    mutationFn: (data) => updateClient(client.id, data),
    onSuccess: (data) => {
      queryClient.setQueryData(['admin', 'client', client.id], data)
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'slack-channels'] })
      onSuccess(data)
    },
    onError: (err) => {
      addToast({ type: 'error', message: err.response?.data?.detail || 'Failed to update client.' })
    },
  })

  function validate() {
    const e = {}
    if (!form.name.trim()) e.name = 'Company name is required'
    if (!form.email.trim()) {
      e.email = 'Email is required'
    } else {
      const addresses = form.email.split(',').map(s => s.trim()).filter(Boolean)
      const invalid = addresses.filter(a => !/\S+@\S+\.\S+/.test(a))
      if (invalid.length) e.email = `Invalid email${invalid.length > 1 ? 's' : ''}: ${invalid.join(', ')}`
    }
    return e
  }

  function toggleStore(storeId) {
    setForm(p => ({
      ...p,
      store_ids: p.store_ids.includes(storeId)
        ? p.store_ids.filter(id => id !== storeId)
        : [...p.store_ids, storeId],
    }))
  }

  function handleChannelChange(channelId) {
    const ch = slackChannels.find(c => c.id === channelId)
    setForm(p => ({
      ...p,
      slack_channel_id:   channelId || '',
      slack_channel_name: ch ? ch.name : '',
    }))
  }

  function handleSubmit(e) {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }
    if (form.archived && !clientData?.archived) {
      if (!window.confirm(`Archive "${form.name}"? They will be hidden from the main dashboard and moved to Archived Clients.`)) return
    }
    mutation.mutate({
      ...form,
      termination_date:   form.termination_date   || null,
      slack_channel_id:   form.slack_channel_id   || null,
      slack_channel_name: form.slack_channel_name || null,
      xero_contact_id:    form.xero_contact_id    || null,
    })
  }

  const isLoading = clientLoading || !form

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <div>
            <h3 className="font-semibold text-slate-800">Edit Client</h3>
            <p className="text-xs text-slate-400 mt-0.5">{client.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-slate-300" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="flex flex-col flex-1 min-h-0">
            <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="label">Company Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Acme Corp Pty Ltd"
                  className={`input ${errors.name ? 'border-red-300 focus:ring-red-400' : ''}`}
                />
                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="label">Nickname</label>
                <input
                  type="text"
                  value={form.nickname}
                  onChange={(e) => setForm(p => ({ ...p, nickname: e.target.value }))}
                  placeholder="Glow Proteins"
                  className="input"
                />
                <p className="text-xs text-slate-400 mt-1">Short display name used throughout the portal</p>
              </div>
              <div>
                <label className="label">Email Address(es) *</label>
                <input
                  type="text"
                  value={form.email}
                  onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="billing@client.com.au, accounts@client.com.au"
                  className={`input ${errors.email ? 'border-red-300 focus:ring-red-400' : ''}`}
                />
                <p className="text-xs text-slate-400 mt-1">Separate multiple addresses with a comma</p>
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="label">Xero Contact ID</label>
                <XeroContactPicker
                  value={form.xero_contact_id}
                  onChange={(v) => setForm(p => ({ ...p, xero_contact_id: v }))}
                />
                <p className="text-xs text-slate-400 mt-1">Optional — can be added later</p>
              </div>
              <div>
                <label className="label">Slack Channel</label>
                <p className="text-xs text-slate-400 mb-2">Link the ops3pl channel for this client to enable warehouse activity sync.</p>
                {channelsLoading ? (
                  <div className="text-xs text-slate-400">Loading channels…</div>
                ) : (
                  <select
                    value={form.slack_channel_id || ''}
                    onChange={e => handleChannelChange(e.target.value)}
                    className="input"
                  >
                    <option value="">— No channel linked —</option>
                    {slackChannels.map(ch => (
                      <option key={ch.id} value={ch.id}>#{ch.name}</option>
                    ))}
                  </select>
                )}
              </div>
              {/* ── Termination & Archive ── */}
              <div className="border-t border-slate-200 pt-4 space-y-4">
                <div>
                  <label className="label">Termination Date</label>
                  <input
                    type="date"
                    value={form.termination_date || ''}
                    onChange={(e) => setForm(p => ({ ...p, termination_date: e.target.value || null }))}
                    className="input"
                  />
                  <p className="text-xs text-slate-400 mt-1">Date all services ceased. Used as the cutoff for final billing.</p>
                </div>
                <div className={`rounded-lg border p-3 ${form.archived ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.archived}
                      onChange={(e) => setForm(p => ({ ...p, archived: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-slate-700">Archive this client</p>
                      <p className="text-xs text-slate-500 mt-0.5">Hides from the main dashboard. Client remains in Archived Clients tab.</p>
                    </div>
                  </label>
                  {form.archived && !form.termination_date && (
                    <p className="text-xs text-amber-600 mt-2 ml-7">⚠ Set a termination date before archiving to ensure accurate final billing.</p>
                  )}
                </div>
              </div>

              <div>
                <label className="label">ShipStation Stores</label>
                <p className="text-xs text-slate-400 mb-2">Select all stores that belong to this client.</p>
                {storesLoading ? (
                  <div className="text-xs text-slate-400">Loading stores…</div>
                ) : (
                  <>
                    {form.store_ids.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {form.store_ids.map(id => {
                          const store = stores.find(s => s.store_id === id)
                          if (!store) return null
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full"
                            >
                              {store.store_name}
                              <button
                                type="button"
                                onClick={() => toggleStore(id)}
                                className="text-blue-400 hover:text-blue-700 transition-colors"
                              >
                                <X size={11} />
                              </button>
                            </span>
                          )
                        })}
                      </div>
                    )}
                    {stores.filter(s => !form.store_ids.includes(s.store_id) && (!s.assigned_client_id || s.assigned_client_id === client.id)).length > 0 ? (
                      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-48 overflow-y-auto">
                        {stores
                          .filter(s => !form.store_ids.includes(s.store_id) && (!s.assigned_client_id || s.assigned_client_id === client.id))
                          .map(store => (
                            <button
                              key={store.store_id}
                              type="button"
                              onClick={() => toggleStore(store.store_id)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-700">{store.store_name}</p>
                                <p className="text-xs text-slate-400">{store.marketplace_name}</p>
                              </div>
                              <Plus size={13} className="text-slate-300 flex-shrink-0" />
                            </button>
                          ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">All stores assigned</p>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex gap-3 justify-end">
              <button type="button" onClick={onClose} className="btn-secondary" disabled={mutation.isPending}>
                Cancel
              </button>
              <button type="submit" disabled={mutation.isPending} className="btn-primary">
                {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />}
                {mutation.isPending ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
        <ToastContainer toasts={toasts} onClose={removeToast} />
      </div>
    </div>
  )
}

function SkeletonRow() {
  return (
    <tr className="border-b border-slate-100">
      {[1,2,3,4,5,6,7].map((i) => (
        <td key={i} className="px-4 py-4">
          <div className="skeleton h-4 rounded w-full" />
        </td>
      ))}
    </tr>
  )
}

export default function AdminClients() {
  const navigate = useNavigate()
  const { toasts, addToast, removeToast } = useToast()
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingClient, setEditingClient] = useState(null)

  const { data: clients = [], isLoading, error } = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: getAdminClients,
  })

  const { data: rollover } = useQuery({
    queryKey: ['admin', 'rollover-summary'],
    queryFn: () => getRolloverSummary(),
    staleTime: 300_000,
    refetchInterval: false,        // opt out of the global 60s poll — this is a
    refetchOnWindowFocus: false,   // multi-table scan, not worth re-running often
  })
  const rolloverBy = Object.fromEntries((rollover?.clients || []).map(r => [r.client_id, r]))


  function handleClientCreated(newClient) {
    addToast({ type: 'success', message: `Client "${newClient.name}" created successfully.` })
    setShowAddModal(false)
  }

  function handleClientUpdated(updated) {
    addToast({ type: 'success', message: `Client "${updated.name}" updated successfully.` })
    setEditingClient(null)
  }

  return (
    <>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Clients</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {isLoading ? 'Loading…' : `${clients.length} client${clients.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            <Plus size={16} />
            Add Client
          </button>
        </div>

        {error ? (
          <div className="card p-8 text-center">
            <AlertCircle size={32} className="mx-auto text-red-300 mb-2" />
            <p className="text-red-500 text-sm">Failed to load clients</p>
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Xero ID</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">MTD Total</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Outstanding (all-time)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Sync</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="w-8 px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {isLoading
                    ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                    : clients.length === 0
                    ? (
                      <tr>
                        <td colSpan={8} className="py-16 text-center">
                          <Users size={32} className="mx-auto text-slate-300 mb-2" />
                          <p className="text-slate-400 text-sm">No clients yet</p>
                          <button
                            onClick={() => setShowAddModal(true)}
                            className="mt-3 btn-primary mx-auto"
                          >
                            <Plus size={14} /> Add first client
                          </button>
                        </td>
                      </tr>
                    )
                    : clients.map((client) => {
                      const isOverdue = !!client.overdue
                      return (
                        <tr
                          key={client.id}
                          onClick={() => navigate(`/admin/clients/${client.id}`)}
                          className="hover:bg-slate-50 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-4">
                            <p className="font-medium text-slate-800 flex items-center gap-2">
                              {client.name}
                              <RolloverBadge amount={rolloverBy[client.id]?.total} items={rolloverBy[client.id]?.items} />
                            </p>
                            {client.nickname && (
                              <p className="text-xs text-slate-400 mt-0.5">{client.nickname}</p>
                            )}
                          </td>
                          <td className="px-4 py-4 text-slate-500">{client.email || '—'}</td>
                          <td className="px-4 py-4">
                            {client.xero_contact_id
                              ? (
                                <span className="font-mono text-xs text-slate-500 truncate max-w-[120px] block">
                                  {client.xero_contact_id}
                                </span>
                              )
                              : <span className="text-slate-300 text-xs">Not set</span>
                            }
                          </td>
                          <td className="px-4 py-4 text-right font-medium text-slate-800">
                            {formatCurrency(client.mtd_total)}
                          </td>
                          <td className="px-4 py-4 text-right">
                            <span className={client.outstanding > 0 ? 'text-red-600 font-semibold' : 'text-slate-700'}>
                              {formatCurrency(client.outstanding)}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-slate-500 text-xs">
                            {safeDate(client.last_sync)}
                          </td>
                          <td className="px-4 py-4 text-center">
                            {isOverdue ? (
                              <span className="badge bg-red-100 text-red-700 text-xs">
                                <AlertCircle size={10} className="mr-1" /> Overdue
                              </span>
                            ) : (
                              <span className="badge bg-green-100 text-green-700 text-xs">Current</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2 justify-end">
                              <button
                                onClick={(e) => { e.stopPropagation(); setEditingClient(client) }}
                                className="p-1 rounded text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                title="Edit client"
                              >
                                <Pencil size={13} />
                              </button>
                              <ChevronRight size={14} className="text-slate-300" />
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  }
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showAddModal && (
        <AddClientModal
          onClose={() => setShowAddModal(false)}
          onSuccess={handleClientCreated}
        />
      )}

      {editingClient && (
        <EditClientModal
          client={editingClient}
          onClose={() => setEditingClient(null)}
          onSuccess={handleClientUpdated}
        />
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </>
  )
}
